// Ünite sınıfı düzenleyicisi — teknik alanlar ve BEYAN PARAMETRELERİ.
//
// Kullanıcı isteği: "ünitelerin beyan edilecek olan özelliklerini revize
// edebilmeli ve yeni özellikler ekleyebilmeliyiz".
//
// Şablonlar veritabanında jsonb metni olarak duruyor. Ham JSON'u kullanıcıya
// yazdırmak yerine satır düzenleyici kuruldu: sıklık ve kaynak kapalı listeden
// seçiliyor, çünkü ikisi de serbest metin olsaydı geçersiz bir değer
// kütüphaneye girip yeni tenant açılışında `beyan_parametresi.frek` CHECK
// kısıtını (K23) patlatırdı — hata da kurulum anında, ilgisiz bir yerde
// görünürdü.
//
// SIKLIK–KAYNAK EŞLEŞMESİ: `olay` ve `lab` sıklıkları kaynağı da belirliyor;
// seçildiklerinde kaynak otomatik ayarlanıp kilitleniyor. Sunucu aynı kuralı
// ayrıca doğruluyor (SablonDogrulama) — burası yalnız kolaylık.

import { useMemo, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { DrawerAltlik, DrawerGovde, useDrawer } from '../../components/Drawer'
import { toast } from '../../components/Toast'
import { ApiHatasi } from '../../lib/api'
import { birimSecenekleri } from '../../lib/birimler'
import { sistem, type SistemUniteSinifi, type UniteGrubu } from './api'

/** K23 sıklık kümesi — sunucudaki Siklik.Gecerli ile aynı. */
const SIKLIKLAR: { deger: string; etiket: string }[] = [
  { deger: '1', etiket: 'Saatlik' },
  { deger: '2', etiket: '2 saatte bir' },
  { deger: '3', etiket: '3 saatte bir' },
  { deger: '4', etiket: '4 saatte bir' },
  { deger: '6', etiket: '6 saatte bir' },
  { deger: '8', etiket: '8 saatte bir' },
  { deger: '12', etiket: '12 saatte bir' },
  { deger: '24', etiket: 'Günlük' },
  { deger: 'olay', etiket: 'Olay bazlı' },
  { deger: 'lab', etiket: 'Laboratuvardan' },
]

const KAYNAKLAR: { deger: string; etiket: string }[] = [
  { deger: 'manuel', etiket: 'Elle girilir' },
  { deger: 'lab', etiket: 'Laboratuvar (salt-okunur)' },
  { deger: 'olay', etiket: 'Olay kaydı' },
]

const GRUPLAR: { deger: UniteGrubu; etiket: string }[] = [
  { deger: 'on', etiket: 'Ön İşlem' },
  { deger: 'cur', etiket: 'Çürütme' },
  { deger: 'depo', etiket: 'Depolama' },
  { deger: 'gaz', etiket: 'Gaz Hattı' },
  { deger: 'enerji', etiket: 'Enerji' },
]

type Teknik = { ad: string; birim: string }
type Beyan = { ad: string; birim: string; frek: string; kaynak: string }

/** jsonb metnini satırlara çevirir; bozuksa boş liste (ekran çökmemeli). */
function coz<T>(ham: string | null | undefined): T[] {
  if (!ham?.trim()) return []
  try {
    const d = JSON.parse(ham)
    return Array.isArray(d) ? (d as T[]) : []
  } catch {
    return []
  }
}

/** Sıklık kaynağı belirliyorsa onu döndürür. */
function kaynakZorunlu(frek: string): string | null {
  if (frek === 'olay') return 'olay'
  if (frek === 'lab') return 'lab'
  return null
}

export default function UniteSinifiDrawer({ mevcut }: { mevcut?: SistemUniteSinifi }) {
  const { kapat } = useDrawer()
  const kuyruk = useQueryClient()
  const duzenleme = !!mevcut

  const [prefix, setPrefix] = useState(mevcut?.kodPrefix ?? '')
  const [ad, setAd] = useState(mevcut?.ad ?? '')
  const [grup, setGrup] = useState<UniteGrubu>(mevcut?.grup ?? 'on')
  const [aciklama, setAciklama] = useState(mevcut?.aciklama ?? '')
  const [sira, setSira] = useState(String(mevcut?.sira ?? 0))
  const [aktif, setAktif] = useState(mevcut?.aktif ?? true)

  const [teknik, setTeknik] = useState<Teknik[]>(() => coz<Teknik>(mevcut?.tSablon))
  const [beyan, setBeyan] = useState<Beyan[]>(() => coz<Beyan>(mevcut?.bSablon))

  // Birimler kutuphaneden (sistem_birim) geliyor. Serbest metin, ayni seyin
  // "t", "ton", "TON" diye uc kez yazilmasina yol aciyordu.
  const { data: birimler = [] } = useQuery({
    queryKey: ['sistem-kut-birim'],
    queryFn: sistem.birimler,
  })

  // Seçenek listesi tesis tarafıyla AYNI yardımcıdan geliyor (lib/birimler):
  // kuralı iki yerde ayrı yazmak, birinde "kayıtta olanı da ekle" adımının
  // unutulmasına açık kapı bırakırdı.
  const secenekler = useMemo(
    () =>
      birimSecenekleri(
        birimler.filter((b) => b.aktif).map((b) => b.kod),
        [...beyan.map((b) => b.birim), ...teknik.map((t) => t.birim)],
      ),
    [birimler, beyan, teknik],
  )

  const engeller: string[] = []
  if (prefix.trim().length < 2 || prefix.trim().length > 8) engeller.push('Kod öneki 2–8 karakter')
  if (ad.trim().length < 2) engeller.push('Sınıf adı girin')
  if (teknik.some((t) => !t.ad.trim())) engeller.push('Adsız teknik alan var')
  if (beyan.some((b) => !b.ad.trim())) engeller.push('Adsız beyan parametresi var')
  const gecerli = engeller.length === 0

  const kaydet = useMutation({
    mutationFn: () =>
      sistem.uniteSinifiYaz(mevcut?.id ?? null, {
        kodPrefix: prefix.trim().toUpperCase(),
        ad: ad.trim(),
        grup,
        aciklama: aciklama.trim() || null,
        tSablon: JSON.stringify(
          teknik.map((t) => ({ ad: t.ad.trim(), birim: t.birim.trim() })),
        ),
        bSablon: JSON.stringify(
          beyan.map((b) => ({
            ad: b.ad.trim(),
            birim: b.birim.trim(),
            frek: b.frek,
            kaynak: b.kaynak,
          })),
        ),
        sira: Number(sira) || 0,
        aktif,
      }),
    onSuccess: (x) => {
      kuyruk.invalidateQueries({ queryKey: ['sistem-kut-sinif'] })
      toast(
        `${x.kodPrefix} kaydedildi · ${beyan.length} beyan parametresi — ` +
          'yalnız bundan sonra açılacak tesislere kopyalanır',
      )
      kapat()
    },
    onError: (h) =>
      toast(
        h instanceof ApiHatasi
          ? h.detay?.length
            ? `${h.message} ${h.detay.join(' · ')}`
            : h.message
          : 'Kaydedilemedi',
      ),
  })

  const beyanDegistir = (i: number, yama: Partial<Beyan>) =>
    setBeyan((liste) =>
      liste.map((b, k) => {
        if (k !== i) return b
        const yeni = { ...b, ...yama }
        // Sıklık kaynağı belirliyorsa kaynağı da düzelt.
        const zorunlu = kaynakZorunlu(yeni.frek)
        if (zorunlu) yeni.kaynak = zorunlu
        else if (b.kaynak !== 'manuel' && kaynakZorunlu(b.frek)) yeni.kaynak = 'manuel'
        return yeni
      }),
    )

  return (
    <>
      <DrawerGovde>
        <div className="fgrid">
          <div className="fld">
            <label htmlFor="us-prefix">Kod öneki</label>
            <input
              id="us-prefix"
              className="in mono"
              value={prefix}
              autoComplete="off"
              onChange={(e) => setPrefix(e.target.value.toUpperCase())}
            />
            <div className="fld-hint">
              Ünite kodları bundan üretiliyor (BS → BS-1) · 2–8 karakter
            </div>
          </div>
          <div className="fld">
            <label htmlFor="us-ad">Sınıf adı</label>
            <input
              id="us-ad"
              className="in"
              value={ad}
              autoComplete="off"
              onChange={(e) => setAd(e.target.value)}
            />
          </div>
          <div className="fld">
            <label htmlFor="us-grup">Grup</label>
            <select
              id="us-grup"
              className="in"
              value={grup}
              onChange={(e) => setGrup(e.target.value as UniteGrubu)}
            >
              {GRUPLAR.map((g) => (
                <option key={g.deger} value={g.deger}>
                  {g.etiket}
                </option>
              ))}
            </select>
          </div>
          <div className="fld">
            <label htmlFor="us-sira">Sıra</label>
            <input
              id="us-sira"
              className="in mono"
              value={sira}
              autoComplete="off"
              onChange={(e) => setSira(e.target.value.replace(/[^\d-]/g, ''))}
            />
          </div>
          <div className="fld" style={{ gridColumn: '1 / -1' }}>
            <label htmlFor="us-aciklama">Açıklama</label>
            <input
              id="us-aciklama"
              className="in"
              value={aciklama}
              autoComplete="off"
              onChange={(e) => setAciklama(e.target.value)}
            />
          </div>
        </div>

        <button
          className="swrow"
          type="button"
          role="switch"
          aria-checked={aktif}
          onClick={() => setAktif((a) => !a)}
        >
          <span>Sınıf açık</span>
          <small>kapalıysa yeni tesislere kopyalanmaz</small>
          <span className={aktif ? 'sw on' : 'sw'} />
        </button>

        {/* ---------------------------------------------- beyan parametreleri */}

        <div className="sect-t">Beyan Parametreleri ({beyan.length})</div>
        <div className="info-box">
          <b>Bu satırlar beyan gridini kuruyor.</b>
          <span>
            Her parametre, seçilen sıklıkta günün saatlerinde hücre açar (K23).
            <span className="mono"> Olay bazlı</span> ve
            <span className="mono"> Laboratuvardan</span> hücre AÇMAZ: ilki olay
            kaydından, ikincisi laboratuvardan salt-okunur akar (K21) — bu yüzden
            ikisi eksik hücre de saymaz.
          </span>
        </div>

        <table className="grid">
          <thead>
            <tr>
              <th>Parametre</th>
              <th style={{ width: 96 }}>Birim</th>
              <th style={{ width: 150 }}>Sıklık</th>
              <th style={{ width: 190 }}>Kaynak</th>
              <th style={{ width: 40 }} />
            </tr>
          </thead>
          <tbody>
            {beyan.length === 0 && (
              <tr>
                <td colSpan={5} className="cell-sub" style={{ padding: 12 }}>
                  Parametre yok — bu sınıftan açılan ünite beyan gridinde satır üretmez.
                </td>
              </tr>
            )}
            {beyan.map((b, i) => {
              const kilitli = kaynakZorunlu(b.frek) !== null
              return (
                <tr key={i}>
                  {/*
                    `.in` tasarim sisteminde YALNIZ `.fld` icinde tanimli
                    (`.fld .in{...}`). Ciplak `.in` hic stil almiyordu ve
                    alanlar tarayici varsayilaniyla, urunun geri kalanindan
                    farkli goruniyordu. Sarmalayici bu yuzden var.
                  */}
                  <td>
                    <div className="fld">
                      <input
                        className="in"
                        value={b.ad}
                        placeholder="Sıcaklık"
                        autoComplete="off"
                        onChange={(e) => beyanDegistir(i, { ad: e.target.value })}
                      />
                    </div>
                  </td>
                  <td>
                    <div className="fld">
                      <select
                        className="in mono"
                        value={b.birim}
                        onChange={(e) => beyanDegistir(i, { birim: e.target.value })}
                      >
                        <option value="">—</option>
                        {secenekler.map((k) => (
                          <option key={k} value={k}>
                            {k}
                          </option>
                        ))}
                      </select>
                    </div>
                  </td>
                  <td>
                    <div className="fld">
                      <select
                        className="in"
                        value={b.frek}
                        onChange={(e) => beyanDegistir(i, { frek: e.target.value })}
                      >
                        {SIKLIKLAR.map((f) => (
                          <option key={f.deger} value={f.deger}>
                            {f.etiket}
                          </option>
                        ))}
                      </select>
                    </div>
                  </td>
                  <td>
                    <div className="fld">
                      <select
                        className="in"
                        value={b.kaynak}
                        disabled={kilitli}
                        title={
                          kilitli ? 'Sıklık kaynağı belirliyor — ayrı seçilemez' : undefined
                        }
                        onChange={(e) => beyanDegistir(i, { kaynak: e.target.value })}
                      >
                        {KAYNAKLAR.map((k) => (
                          <option key={k.deger} value={k.deger}>
                            {k.etiket}
                          </option>
                        ))}
                      </select>
                    </div>
                  </td>
                  <td>
                    <button
                      className="mini-btn"
                      type="button"
                      title="Parametreyi kaldır"
                      onClick={() => setBeyan((l) => l.filter((_, k) => k !== i))}
                    >
                      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M18 6 6 18M6 6l12 12" />
                      </svg>
                    </button>
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>

        <div style={{ padding: '8px 0' }}>
          <button
            className="btn btn-sec btn-sm"
            type="button"
            onClick={() =>
              setBeyan((l) => [...l, { ad: '', birim: '', frek: '24', kaynak: 'manuel' }])
            }
          >
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M12 5v14M5 12h14" />
            </svg>
            Beyan parametresi ekle
          </button>
        </div>

        {/* -------------------------------------------------- teknik alanlar */}

        <div className="sect-t">Teknik Alanlar ({teknik.length})</div>
        <div className="fld-hint" style={{ padding: '0 0 6px' }}>
          Ünitenin sabit özellikleri (hacim, rejim…). Beyan gridinde hücre AÇMAZ;
          ünite kartında değer olarak durur.
        </div>

        <table className="grid">
          <thead>
            <tr>
              <th>Alan</th>
              <th style={{ width: 140 }}>Birim</th>
              <th style={{ width: 40 }} />
            </tr>
          </thead>
          <tbody>
            {teknik.length === 0 && (
              <tr>
                <td colSpan={3} className="cell-sub" style={{ padding: 12 }}>
                  Teknik alan yok.
                </td>
              </tr>
            )}
            {teknik.map((t, i) => (
              <tr key={i}>
                <td>
                  <div className="fld">
                    <input
                      className="in"
                      value={t.ad}
                      placeholder="Hazne hacmi"
                      autoComplete="off"
                      onChange={(e) =>
                        setTeknik((l) =>
                          l.map((x, k) => (k === i ? { ...x, ad: e.target.value } : x)),
                        )
                      }
                    />
                  </div>
                </td>
                <td>
                  <div className="fld">
                    <select
                      className="in mono"
                      value={t.birim}
                      onChange={(e) =>
                        setTeknik((l) =>
                          l.map((x, k) => (k === i ? { ...x, birim: e.target.value } : x)),
                        )
                      }
                    >
                      <option value="">—</option>
                      {secenekler.map((k) => (
                        <option key={k} value={k}>
                          {k}
                        </option>
                      ))}
                    </select>
                  </div>
                </td>
                <td>
                  <button
                    className="mini-btn"
                    type="button"
                    title="Alanı kaldır"
                    onClick={() => setTeknik((l) => l.filter((_, k) => k !== i))}
                  >
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M18 6 6 18M6 6l12 12" />
                    </svg>
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        <div style={{ padding: '8px 0 4px' }}>
          <button
            className="btn btn-sec btn-sm"
            type="button"
            onClick={() => setTeknik((l) => [...l, { ad: '', birim: '' }])}
          >
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M12 5v14M5 12h14" />
            </svg>
            Teknik alan ekle
          </button>
        </div>

        {engeller.length > 0 && (
          <div
            className="info-box"
            style={{ borderColor: 'var(--red-bd)', background: 'var(--red-bg)' }}
          >
            <b>Kaydedilemez.</b>
            <span>{engeller.join(' · ')}</span>
          </div>
        )}

        <div className="rule">
          <b>Not</b>
          <span>
            Değişiklik <b>mevcut tesisleri etkilemez</b> — yalnız bundan sonra
            açılacak tesislere kopyalanır. Var olan bir şirkete eklemek için
            Şirketler ekranındaki "Kütüphaneyi tamamla" düğmesini kullanın.
          </span>
        </div>
      </DrawerGovde>

      <DrawerAltlik>
        <button className="btn btn-ghost" type="button" onClick={() => kapat()}>
          Vazgeç
        </button>
        <button
          className="btn btn-pri"
          type="button"
          style={{ flex: 1, justifyContent: 'center' }}
          disabled={!gecerli || kaydet.isPending}
          title={gecerli ? undefined : `Eksik: ${engeller.join(' · ')}`}
          onClick={() => kaydet.mutate()}
        >
          {duzenleme ? 'Kaydet' : 'Sınıfı ekle'}
        </button>
      </DrawerAltlik>
    </>
  )
}
